/**
 * SHARED DATA GRID
 *
 * Provides sortable, editable, searchable, persistent AG Grid tables for CRM entities.
 *
 * Changes:
 * - 2026-09-01: Supports placing the existing row and column actions in a supplied header portal
 * - 2026-09-01: Selects the Alpine light/dark theme object from the application theme
 * - 2026-08-06: Makes Between filters inclusive for every normalized date column.
 * - 2026-08-18: Keeps column-filter popups mounted during live filtering by deferring parent export callbacks until dismissal.
 * - 2026-08-25: Keeps every body cell to one line and shows an ellipsis for overflowing values.
 * - 2026-08-27: Raises shared textarea editors to the 65,536-character Note cap.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AgGridReact } from 'ag-grid-react';
import './agGridModules'; // Register AG Grid modules
import ColumnSelector from './ColumnSelector';
import {
  ColDef,
  ICellRendererParams,
  GridApi,
  ValueSetterParams,
  IRowNode,
  RowDataTransaction,
  ColumnState,
  ColumnPinnedType,
  SortDirection,
  ColumnMenuVisibleChangedEvent,
  colorSchemeDark,
  colorSchemeLight,
  themeAlpine,
} from 'ag-grid-community';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { NOTE_MAX_LENGTH } from '../../constants/noteConstants';
import { useTheme } from '../../context/ThemeContext';

interface GridState {
  columnState: ColumnState[];
  filterModel: any;
  sortState: ColumnState[];
}

interface MessageProps {
  text: string;
  isError: boolean;
}

export interface CustomRenderers<T> {
  [key: string]: (props: ICellRendererParams<T>) => React.ReactElement | null;
}

function normalizeSortDirection(sort: unknown): SortDirection | undefined {
  return sort === 'asc' || sort === 'desc' ? sort : undefined;
}

interface GridProps<T extends Record<string, any>> {
  message?: MessageProps;
  isSaving?: boolean;
  data: T[];
  columnDefs: ColDef<T>[];
  searchQuery?: string;
  name?: string; // Unique identifier for persisting grid state to localStorage
  onColumnStateChanged?: (newDefs: ColDef<T>[]) => void;
  getRowId?: (data: T) => string;
  onValueChanged?: (data: T[]) => void;
  enableRowDrag?: boolean;
  onAddRow?: () => void;
  canAddRows?: boolean;
  canRemoveRows?: boolean;
  gridOptions?: any;
  columnSelectorPortalTargetId?: string; // ID of the element to portal the column selector into
  toolbarPortalTargetId?: string; // ID of the element to portal row actions and the column selector into
  onFilteredDataChange?: (filteredData: T[], filterModel: any) => void;
}

type GridDateValue = string | number | Date | null | undefined;

const GRID_LIGHT_THEME = themeAlpine.withPart(colorSchemeLight);
const GRID_DARK_THEME = themeAlpine.withPart(colorSchemeDark);

function parseGridDateValue(value: GridDateValue): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }

    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === 'number') {
    const parsedFromNumber = new Date(value);
    if (Number.isNaN(parsedFromNumber.getTime())) {
      return null;
    }

    return new Date(
      parsedFromNumber.getFullYear(),
      parsedFromNumber.getMonth(),
      parsedFromNumber.getDate()
    );
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  const dateOnlyMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const parsedDateOnly = new Date(Number(year), Number(month) - 1, Number(day));

    if (Number.isNaN(parsedDateOnly.getTime())) {
      return null;
    }

    return parsedDateOnly;
  }

  const parsedDateTime = new Date(trimmedValue);
  if (Number.isNaN(parsedDateTime.getTime())) {
    return null;
  }

  return new Date(
    parsedDateTime.getFullYear(),
    parsedDateTime.getMonth(),
    parsedDateTime.getDate()
  );
}

function formatGridDateValue(value: GridDateValue): string {
  const parsedDate = parseGridDateValue(value);
  if (!parsedDate) {
    return typeof value === 'string' ? value : '';
  }

  return parsedDate.toLocaleDateString();
}

function compareGridDateFilterValues(filterLocalDateAtMidnight: Date, cellValue: GridDateValue): number {
  const parsedDate = parseGridDateValue(cellValue);

  if (!parsedDate) {
    return 0;
  }

  const cellTime = parsedDate.getTime();
  const filterTime = filterLocalDateAtMidnight.getTime();

  if (cellTime === filterTime) {
    return 0;
  }

  return cellTime < filterTime ? -1 : 1;
}

function hasDateColumnType(columnType: ColDef['type']): boolean {
  if (Array.isArray(columnType)) {
    return columnType.includes('date');
  }

  return columnType === 'date';
}

export function normalizeGridDateColumnDef<TData extends Record<string, any>>(columnDef: ColDef<TData>): ColDef<TData> {
  if (!hasDateColumnType(columnDef.type)) {
    return columnDef;
  }

  return {
    ...columnDef,
    cellDataType: false,
    filterParams: {
      browserDatePicker: true,
      ...(columnDef.filterParams || {}),
      inRangeInclusive: true,
    }
  };
}

function Grid<T extends Record<string, any> & { id: number | string }>({
  data,
  columnDefs,
  searchQuery = '',
  onColumnStateChanged,
  getRowId = (data: T) => String(data.id),
  onValueChanged,
  enableRowDrag = false,
  onAddRow,
  canAddRows = false,
  canRemoveRows = false,
  gridOptions = {},
  message,
  isSaving,
  name,
  columnSelectorPortalTargetId,
  toolbarPortalTargetId,
  onFilteredDataChange
}: GridProps<T>) {
  const { isDark } = useTheme();
  const [globalFilter, setGlobalFilter] = useState<string[]>([]);
  const [hasSelectedRows, setHasSelectedRows] = useState(false);
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [orderedColumns, setOrderedColumns] = useState<ColDef<T>[]>([]);
  const gridApi = useRef<GridApi<T> | null>(null);
  const isColumnFilterMenuOpenRef = useRef(false);
  const pendingFilteredDataRef = useRef<{ filteredRows: T[]; filterModel: any } | null>(null);

  // Debounced save to prevent excessive localStorage writes
  const debouncedSaveRef = useRef<any>(null);

  // Function to set restore in progress state
  const setRestoreState = useCallback((inProgress: boolean) => {
    setRestoreInProgress(inProgress);
  }, []);

  const saveGridState = useCallback(() => {
    if (!name || !gridApi.current || restoreInProgress) return;

    // Clear any pending debounced save
    if (debouncedSaveRef.current) {
      clearTimeout(debouncedSaveRef.current);
    }

    // Set a new timeout to save after 300ms
    debouncedSaveRef.current = setTimeout(() => {
      try {
        const columnState = gridApi.current?.getColumnState() || [];
        const currentColumns = gridApi.current?.getColumnDefs() as ColDef<T>[] || [];

        // Enhance column state
        const enhancedColumnState: ColumnState[] = columnState.map(state => {
          const colDef = currentColumns.find(def =>
            def.colId === state.colId || def.field === state.colId
          );

          return {
            colId: state.colId,
            field: colDef?.field,
            width: state.width,
            hide: state.hide || false,
            pinned: state.pinned as ColumnPinnedType,
            sort: normalizeSortDirection(state.sort),
            sortIndex: state.sortIndex
          };
        });

        // Extract sort state
        const sortState = columnState
          .filter(col => col.sort)
          .map(col => ({
            colId: col.colId,
            sort: normalizeSortDirection(col.sort),
            sortIndex: col.sortIndex || 0
          }));

        const state: GridState = {
          columnState: enhancedColumnState,
          filterModel: gridApi.current?.getFilterModel() || {},
          sortState
        };

        localStorage.setItem(`grid_state_${name}`, JSON.stringify(state));
      } catch (error) {
        console.error('Error saving grid state:', error);
      }
    }, 300);
  }, [name, restoreInProgress]);

  // Enhanced filter changed handler that includes filtered data extraction
  const onFilterChanged = useCallback(() => {
    saveGridState(); // Existing functionality

    // NEW: Handle filtered data callback
    if (onFilteredDataChange && gridApi.current) {
      const filteredRows: T[] = [];
      gridApi.current.forEachNodeAfterFilterAndSort((node) => {
        if (node.data) {
          filteredRows.push(node.data);
        }
      });

      const filterModel = gridApi.current.getFilterModel();
      if (isColumnFilterMenuOpenRef.current) {
        pendingFilteredDataRef.current = { filteredRows, filterModel };
      } else {
        onFilteredDataChange(filteredRows, filterModel);
      }
    }
  }, [saveGridState, onFilteredDataChange]);

  const onColumnMenuVisibleChanged = useCallback((event: ColumnMenuVisibleChangedEvent<T>) => {
    const isFilterMenu = ['columnFilter', 'floatingFilter', 'filterMenuTab', 'columnMenu'].includes(event.key);

    if (isFilterMenu && event.visible) {
      isColumnFilterMenuOpenRef.current = true;
    }

    if (!event.visible && !event.switchingTab && isColumnFilterMenuOpenRef.current) {
      isColumnFilterMenuOpenRef.current = false;

      if (pendingFilteredDataRef.current && onFilteredDataChange) {
        const { filteredRows, filterModel } = pendingFilteredDataRef.current;
        pendingFilteredDataRef.current = null;
        onFilteredDataChange(filteredRows, filterModel);
      }
    }

    gridOptions.onColumnMenuVisibleChanged?.(event);
  }, [gridOptions, onFilteredDataChange]);

  const getAllRowData = useCallback(() => {
    if (!gridApi.current) return [];
    const rowCount = gridApi.current.getDisplayedRowCount();
    const rows = [];
    for (let i = 0; i < rowCount; i++) {
      const rowNode = gridApi.current.getDisplayedRowAtIndex(i);
      if (rowNode && rowNode.data) {
        rows.push(rowNode.data);
      }
    }
    return rows;
  }, []);

  const handleDataChange = useCallback(() => {
    if (onValueChanged && gridApi.current) {
      const allRows = getAllRowData();
      onValueChanged(allRows);
    }
  }, [onValueChanged, getAllRowData]);

  const defaultColDef: ColDef<T> = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
      editable: true,
      minWidth: 100,
      maxWidth: 1000,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      menuTabs: [],
      wrapText: false,
      autoHeight: false,
      cellStyle: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: '1.5',
        display: 'flex',
        alignItems: 'center'
      },
      hide: false,
      field: undefined,
      valueSetter: (params: ValueSetterParams<T>) => {
        const field = params.colDef.field;
        if (!field || !params.data) return false;

        const data = params.data as Record<string, any>;
        data[field] = params.newValue;
        return true;
      },
      cellEditor: 'agTextCellEditor',
      valueFormatter: (params) => {
        if (params.colDef.type === 'date' && params.value) {
          return formatGridDateValue(params.value as GridDateValue);
        }
        return params.value;
      }
    };
  }, []);

  useEffect(() => {
    const keywords = searchQuery ? searchQuery.toLowerCase().split(' ').filter(Boolean) : [];
    setGlobalFilter(keywords);
  }, [searchQuery]);

  const isExternalFilterPresent = useCallback(() => globalFilter.length > 0, [globalFilter]);

  const doesExternalFilterPass = useCallback((node: IRowNode<T>) => {
    const data = node.data;
    if (!data || !globalFilter.length) return !globalFilter.length;

    const searchableText = Object.values(data)
      .map(value => value ? String(value).toLowerCase() : '')
      .join(' ');
    return globalFilter.every(keyword => searchableText.includes(keyword));
  }, [globalFilter]);

  // Function to merge saved state with column definitions
  const getSavedColumnState = useCallback(() => {
    if (!name) return null;

    try {
      const savedState = localStorage.getItem(`grid_state_${name}`);
      if (!savedState) return null;

      const state: GridState = JSON.parse(savedState);
      return state.columnState?.length ? state.columnState : null;
    } catch (error) {
      console.error('Error loading saved column state:', error);
      return null;
    }
  }, [name]);

  // Initialize orderedColumns when columnDefs changes, applying saved state if available
  useEffect(() => {
    const savedColumnState = getSavedColumnState();

    if (!savedColumnState) {
      // No saved state, show all columns
      setOrderedColumns(columnDefs.map(col => ({
        ...normalizeGridDateColumnDef(col),
        hide: false // Ensure all columns are visible by default
      })));
      return;
    }

    // Merge saved state with column definitions
    const mergedColumns = columnDefs.map(rawColDef => {
      const colDef = normalizeGridDateColumnDef(rawColDef);
      const colId = colDef.colId || colDef.field || '';
      const savedCol = savedColumnState.find(state =>
        state.colId === colId
      );

      if (savedCol) {
        // Apply saved state properties to column definition
        return {
          ...colDef,
          width: savedCol.width || colDef.width,
          hide: savedCol.hide === true || savedCol.hide === false ? savedCol.hide : false, // Apply saved visibility, ensuring it's a boolean
          pinned: savedCol.pinned || colDef.pinned,
          sort: normalizeSortDirection(savedCol.sort) ?? normalizeSortDirection(colDef.sort),
          sortIndex: savedCol.sortIndex
        };
      }

      // No saved state for this column, keep it visible
      return {
        ...colDef,
        hide: false
      };
    });

    setOrderedColumns(mergedColumns);
  }, [columnDefs, getSavedColumnState]);

  const updateColumnState = useCallback(() => {
    if (!gridApi.current || !onColumnStateChanged) return;

    // Get the current column state including width, position, etc.
    const columnState = gridApi.current.getColumnState();
    const currentColumnDefs = gridApi.current.getColumnDefs() as ColDef<T>[];

    if (columnState && currentColumnDefs) {
      // Merge column state with column definitions
      const updatedDefs = currentColumnDefs.map(colDef => {
        const state = columnState.find(s => s.colId === (colDef.field || colDef.colId));
        if (state) {
          return {
            ...colDef,
            width: state.width,
            hide: state.hide,
            pinned: state.pinned,
            sort: normalizeSortDirection(state.sort),
            sortIndex: state.sortIndex
          } as ColDef<T>;
        }
        return colDef;
      });

      onColumnStateChanged(updatedDefs);
      setOrderedColumns(updatedDefs);
    }
  }, [onColumnStateChanged]);

  const onColumnMoved = useCallback(() => {
    updateColumnState();
    saveGridState();
  }, [updateColumnState, saveGridState]);

  const onColumnResized = useCallback(() => {
    updateColumnState();
    saveGridState();
  }, [updateColumnState, saveGridState]);

  const onColumnVisible = useCallback(() => {
    updateColumnState();
    saveGridState();
  }, [updateColumnState, saveGridState]);

  const onColumnPinned = useCallback(() => {
    updateColumnState();
    saveGridState();
  }, [updateColumnState, saveGridState]);

  const onSortChanged = useCallback(() => {
    updateColumnState();
    saveGridState();
  }, [updateColumnState, saveGridState]);

  const onGridReady = useCallback((params: { api: GridApi<T> }) => {
    gridApi.current = params.api;
    params.api.addEventListener('selectionChanged', () => {
      setHasSelectedRows(params.api.getSelectedRows().length > 0);
    });

    // Set restore in progress flag to prevent saving while restoring
    setRestoreState(true);

    // Wait for next tick to ensure grid is fully initialized
    setTimeout(() => {
      if (name && gridApi.current) {
        const savedState = localStorage.getItem(`grid_state_${name}`);
        if (savedState) {
          try {
            const state: GridState = JSON.parse(savedState);

            // First ensure columns have proper types and filters
            const initialColumnState: ColumnState[] = orderedColumns.map(col => ({
              colId: col.field || col.colId || '',
              width: col.width,
              hide: col.hide || false,
              pinned: col.pinned as ColumnPinnedType,
              sort: normalizeSortDirection(col.sort),
              sortIndex: col.sortIndex || 0
            }));

            gridApi.current.applyColumnState({
              state: initialColumnState,
              applyOrder: true
            });

            // Then restore saved state
            if (state.columnState?.length) {
              gridApi.current.applyColumnState({
                state: state.columnState,
                applyOrder: true
              });
            }

            // Small delay to ensure columns are ready before applying filters and sorts
            setTimeout(() => {
              if (gridApi.current) {
                // Apply filter model
                if (state.filterModel && Object.keys(state.filterModel).length > 0) {
                  gridApi.current.setFilterModel(state.filterModel);
                }

                // Apply sort state
                if (state.sortState?.length) {
                  gridApi.current.applyColumnState({
                    state: state.sortState,
                    defaultState: { sort: null }
                  });
                }
              }
            }, 100);
          } catch (error) {
            console.error('Error applying additional grid state:', error);
          }
        }
        // Mark restore as complete
        setRestoreState(false);
      }
    }, 0);
  }, [name]);

  const createDefaultRow = useCallback(() => {
    const newRow: Partial<T> = {};

    const tempId = Math.min(...data.map(item => Number(item.id)), 0) - 1;
    (newRow as any).id = tempId;

    columnDefs.forEach((colDef) => {
      if (colDef.field) {
        let defaultValue: any = null;

        switch (colDef.type) {
          case 'number':
            defaultValue = 0;
            break;
          case 'date':
            defaultValue = new Date().toISOString();
            break;
          case 'boolean':
            defaultValue = false;
            break;
          case 'select':
            defaultValue = colDef.cellEditorParams?.values?.[0] || '';
            break;
          default:
            defaultValue = '';
        }

        (newRow as any)[colDef.field] = defaultValue;
      }
    });

    return newRow as T;
  }, [data, columnDefs]);

  const handleAddRow = useCallback(() => {
    if (onAddRow) {
      onAddRow();
    } else if (gridApi.current) {
      const newRow = createDefaultRow();
      const transaction: RowDataTransaction<T> = {
        add: [newRow],
        addIndex: 0
      };
      gridApi.current.applyTransaction(transaction);
      handleDataChange();
    }
  }, [onAddRow, createDefaultRow, handleDataChange]);

  const handleRemoveRow = useCallback(() => {
    if (gridApi.current) {
      const selectedRows = gridApi.current.getSelectedRows();
      if (selectedRows.length > 0) {
        const rowCount = selectedRows.length;
        const confirmMessage = rowCount === 1
          ? 'Are you sure you want to delete this row?'
          : `Are you sure you want to delete ${rowCount} rows?`;

        if (window.confirm(confirmMessage)) {
          const transaction: RowDataTransaction<T> = {
            remove: selectedRows
          };
          gridApi.current.applyTransaction(transaction);
          handleDataChange();
        }
      }
    }
  }, [handleDataChange]);

  // Save grid state when data or columns change
  useEffect(() => {
    if (gridApi.current && name && !restoreInProgress) {
      // Delay to ensure the grid has processed the new data
      const timer = setTimeout(() => {
        // Ensure grid API column state matches our React state
        if (gridApi.current) {
          const gridColumnState = gridApi.current.getColumnState();
          const reactColumnState = orderedColumns.map(col => ({
            colId: col.colId || col.field || '',
            hide: col.hide
          }));

          // Check if any columns have mismatched visibility
          const mismatch = reactColumnState.some(reactCol => {
            const gridCol = gridColumnState.find(gc => gc.colId === reactCol.colId);
            return gridCol && gridCol.hide !== reactCol.hide;
          });

          // If there's a mismatch, apply our React state to the grid
          if (mismatch) {
            gridApi.current.applyColumnState({
              state: reactColumnState,
              applyOrder: false
            });
          }
        }

        saveGridState();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [data, columnDefs, saveGridState, name, restoreInProgress, orderedColumns]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debouncedSaveRef.current) {
        clearTimeout(debouncedSaveRef.current);
      }
    };
  }, []);

  const toolbarPortalTarget = toolbarPortalTargetId && typeof document !== 'undefined'
    ? document.getElementById(toolbarPortalTargetId)
    : null;

  const columnSelector = (
    <ColumnSelector<T>
      isOpen={isColumnSelectorOpen}
      setIsOpen={setIsColumnSelectorOpen}
      columnDefs={columnDefs}
      orderedColumns={orderedColumns}
      triggerClassName={toolbarPortalTarget ? '!h-8 !py-1 !leading-5' : undefined}
      onStateUpdated={(newDefs) => {
        setOrderedColumns(newDefs);
        if (onColumnStateChanged) {
          onColumnStateChanged(newDefs);
        }
      }}
    />
  );

  const rowActionButtons = (
    <>
      {canRemoveRows && (
        <button
          onClick={handleRemoveRow}
          disabled={!hasSelectedRows}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium ${THEME_CLASSES.button.danger} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          Remove Row
        </button>
      )}
      {canAddRows && (
        <button
          onClick={handleAddRow}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium ${THEME_CLASSES.button.primary}`}
        >
          Add Row
        </button>
      )}
    </>
  );

  return (
    <div className="h-full w-full flex flex-col">
      {(canAddRows || canRemoveRows) && (!toolbarPortalTarget || isSaving || message || hasSelectedRows) && (
        <div className="flex items-center justify-between mb-2 px-2">
          <div className="flex items-center flex-1">
            {isSaving && (
              <span className="text-sm text-blue-600 dark:text-blue-400">
                Saving changes...
              </span>
            )}
            {message && (
              <span className={`text-sm ml-4 ${message.isError ? THEME_CLASSES.status.error : THEME_CLASSES.status.success}`}>
                {message.text}
              </span>
            )}
            {hasSelectedRows && (
              <span className="text-sm text-gray-600 dark:text-gray-400 ml-4">
                {`${gridApi.current?.getSelectedRows().length} ${gridApi.current?.getSelectedRows().length === 1 ? 'row' : 'rows'} selected`}
              </span>
            )}
          </div>
          {!toolbarPortalTarget && <div className="flex gap-2">{rowActionButtons}</div>}
        </div>
      )}
      {toolbarPortalTarget && createPortal(
        <div className="flex items-center gap-2 whitespace-nowrap" data-testid="grid-header-actions">
          {rowActionButtons}
          {columnSelector}
        </div>,
        toolbarPortalTarget
      )}
      <div className="flex-1 w-full flex flex-col">
        {!toolbarPortalTarget && (columnSelectorPortalTargetId ? (
          // Render through portal if target is specified
          (() => {
            const target = document.getElementById(columnSelectorPortalTargetId);
            return target ? createPortal(columnSelector, target) : null;
          })()
        ) : (
          // Default rendering location
          <div className="mb-2 flex justify-end px-2">
            {columnSelector}
          </div>
        ))}
        <div
          className="flex-1 w-full"
          data-grid-theme={isDark ? 'dark' : 'light'}
          data-testid="shared-grid-theme"
        >
          <AgGridReact<T>
            theme={isDark ? GRID_DARK_THEME : GRID_LIGHT_THEME}
            columnDefs={orderedColumns}
            rowData={data}
            defaultColDef={defaultColDef}
            suppressColumnMoveAnimation={true}
            rowDragManaged={enableRowDrag}
            animateRows={enableRowDrag}
            onRowDragEnd={handleDataChange}
            {...(enableRowDrag && {
              rowDragEntireRow: false,
              rowDragMultiRow: false
            })}
            columnTypes={{
              text: {
                filter: 'agTextColumnFilter',
                cellEditor: 'agTextCellEditor'
              },
              number: {
                filter: 'agNumberColumnFilter',
                cellEditor: 'agTextCellEditor'
              },
              date: {
                filter: 'agDateColumnFilter',
                cellEditor: 'agDateCellEditor',
                cellEditorParams: {
                  browserDatePicker: true
                },
                filterParams: {
                  browserDatePicker: true,
                  inRangeInclusive: true,
                  comparator: (filterLocalDateAtMidnight: Date, cellValue: GridDateValue) => {
                    return compareGridDateFilterValues(filterLocalDateAtMidnight, cellValue);
                  },
                  isValidDate: (cellValue: GridDateValue) => parseGridDateValue(cellValue) !== null,
                },
                valueFormatter: (params) => formatGridDateValue(params.value as GridDateValue)
              },
              select: {
                filter: 'agTextColumnFilter',
                cellEditor: 'agSelectCellEditor'
              },
              textarea: {
                filter: 'agTextColumnFilter',
                cellEditor: 'agLargeTextCellEditor',
                cellEditorPopup: true,
                cellEditorParams: {
                  maxLength: NOTE_MAX_LENGTH,
                  rows: 10,
                  cols: 50
                },
                autoHeight: false,
                wrapText: false,
                cellStyle: {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.5',
                  display: 'flex',
                  alignItems: 'center'
                }
              },
            }}
            onGridReady={onGridReady}
            onCellValueChanged={handleDataChange}
            getRowId={(params) => getRowId(params.data)}
            className="h-full w-full"
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
            gridOptions={gridOptions}
            suppressMultiSort={true}
            alwaysShowHorizontalScroll={true}
            alwaysShowVerticalScroll={true}
            onFilterChanged={onFilterChanged}
            onColumnMenuVisibleChanged={onColumnMenuVisibleChanged}
            onSortChanged={onSortChanged}
            onColumnResized={onColumnResized}
            onColumnVisible={onColumnVisible}
            onColumnPinned={onColumnPinned}
            onColumnMoved={onColumnMoved}
            onColumnRowGroupChanged={saveGridState}
            enableCellTextSelection={true}
            maintainColumnOrder={true}
          // pagination={true}
          />
        </div>
      </div>
    </div>
  );
}

export default Grid;
